import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

export default function SalicylateToxicityTracker({user}) {
  // Demographic and Clinical States
  const [patientGroup, setPatientGroup] = useState('adult'); // adult vs vulnerable (child/elderly)
  const [hoursPostIngestion, setHoursPostIngestion] = useState('');
  const [hasSevereFeatures, setHasSevereFeatures] = useState('no'); // coma, convulsions, pulmonary edema, ARF
  const [bloodPh, setBloodPh] = useState('7.40');

  // Serial Level States
  const [level1, setLevel1] = useState('');
  const [level2, setLevel2] = useState('');
  const [level3, setLevel3] = useState('');

  // Triage Results State
  const [triage, setTriage] = useState({
    status: 'Awaiting Core Values',
    severity: 'Unknown',
    peaked: false,
    recommendations: [],
    color: '#95a5a6'
  });

  useEffect(() => {
    const hrs = parseFloat(hoursPostIngestion);
    const l1 = parseFloat(level1);
    const l2 = parseFloat(level2);
    const l3 = parseFloat(level3);
    const ph = parseFloat(bloodPh);

    let recs = [];
    let severity = 'Awaiting Inputs';
    let status = 'Pending Lab Values';
    let color = '#95a5a6';
    let peaked = false;

    // 🛑 Rule 1: Guarding the 4-Hour Ingestion Floor
    if (hrs && hrs < 4) {
      setTriage({
        status: '⏳ Pre-Sampling Window Restriction',
        severity: 'Inconclusive',
        peaked: false,
        color: '#f1c40f',
        recommendations: [
          '⚠️ CRITICAL PROTOCOL TIMING: Your handbook notes that definitive blood assays should be drawn AT LEAST 4 hours after ingestion.',
          '🔄 Plan supportive fluid rehydration right now and set a hard timer to draw the baseline sample once the 4-hour post-ingestion mark is crossed.'
        ]
      });
      return;
    }

    if (!l1) {
      setTriage({ status, severity, peaked, recommendations: ['Please input at least the first Salicylate level to initiate tracking.'], color });
      return;
    }

    // Determine Peak Status and Target Value Tracking
    let activeLevel = l1;
    if (l2) activeLevel = l2;
    if (l3) activeLevel = l3;

    if (l2 && !l3) {
      if (l2 < l1) peaked = true;
    } else if (l2 && l3) {
      if (l3 < l2) peaked = true;
    }

    // 🚨 Emergency Branch: Immediate Severe Clinical Features Present (tdm4.png)
    if (hasSevereFeatures === 'yes') {
      severity = 'CRITICAL SEVERE POISONING';
      status = '🚨 Immediate Life Support Activation Required';
      color = '#e74c3c';
      recs.push(
        '🔴 EMERGENCY CRITERIA ACTIVATED: Patient presents with high-alert systemic signs (Coma, Convulsions, Pulmonary Edema, or Acute Renal Failure).',
        '🚨 ACTION: Resuscitate immediately (Airway, Breathing, Circulation). Check ABGs immediately.',
        '🏥 INITIATE EMERGENCY HAEMODIALYSIS: Do not wait for further serial levels. Contact your poisons unit and ICU team immediately.'
      );
    } 
    // 🩸 Emergency Branch: Severe Metabolic Acidosis (tdm4.png / tdm5.png)
    else if (ph < 7.2) {
      severity = 'CRITICAL METABOLIC CRISIS';
      status = '🚨 Urgent Extracorporeal Elimination Required';
      color = '#e74c3c';
      recs.push(
        '🔴 CRITICAL ACIDEMIA DETECTED (pH < 7.2): High risk of uncoupled salicylate molecules freely penetrating the blood-brain barrier.',
        '💉 ACTION: Administer 1 mL/kg of 8.4% Sodium Bicarbonate IV immediately to shift pH back towards 7.4.',
        '🏥 CONSIDER HAEMODIALYSIS: Severe acidemia overriding compensation is an absolute trigger for urgent haemodialysis.'
      );
    }
    // 📊 Standard Stratification Matrix based on Patient Group thresholds (tdm5.png)
    else {
      const isVulnerable = patientGroup === 'vulnerable';
      const toxicLine = isVulnerable ? 200 : 300;
      const moderateLine = isVulnerable ? 450 : 600;
      const severeLine = isVulnerable ? 700 : 800;

      if (activeLevel < toxicLine) {
        severity = 'Asymptomatic / Mild Exposure';
        color = '#2ecc71';
        status = '✅ Under Conservative Management Limits';
        recs.push(
          '💧 Action: Rehydrate the patient carefully with oral or intravenous fluids.',
          '📈 Monitor core urine output and maintain a close fluid balance ledger.'
        );
      } else if (activeLevel >= toxicLine && activeLevel < moderateLine) {
        severity = 'Mild Poisoning';
        color = '#f1c40f';
        status = '⚠️ Serial Observation Track Active';
        recs.push(
          'ℹ️ Clinical Manifestations: Watch for signs of lethargy, nausea, vomiting, tinnitus, and mild dizziness.',
          '💧 Treatment: Rehydrate with oral or intravenous fluids. Monitor baseline vitals closely.'
        );
      } else if (activeLevel >= moderateLine && activeLevel < severeLine) {
        severity = 'Moderate Poisoning';
        color = '#e67e2 orange';
        status = '⚠️ Initiate Active Elimination Enhancements';
        color = '#e67e22';
        recs.push(
          'ℹ️ Clinical Manifestations: Associated with mild hyperpyrexia, sweating, tachycardia/tachypnoea, dehydration, or mild loss of coordination.',
          '🧪 INITIATE IV URINARY ALKALINIZATION: Adults: Give 1L of 1.26% Sodium Bicarbonate with 20 to 40 mmol Potassium as an IV infusion over 3 hours. Children: Dilute 1 ml/kg 8.4% bicarb in 10 ml/kg saline, adding 1 mmol/kg potassium. This should be given at a rate of 2ml/kg/h as an iv infusion',
          '🎯 Monitoring Goal: Check urinary pH hourly, target a high alkaline ceiling of 7.5 – 8.5. Keep serum potassium strictly inside 4.0 – 4.5 mmol/L (check U&E every 3 hours).'
        );
      } else {
        severity = 'Severe Poisoning';
        color = '#e74c3c';
        status = '🚨 Critical Threshold Breach';
        recs.push(
          'ℹ️ Clinical Manifestations: High risk of hypotension, severe metabolic acidosis, renal failure (oliguria), and advanced CNS features (hallucinations, seizures, stupor, coma).',
          '🧪 MAXIMIZE INTRAVENOUS NaHCO3 ALKALINIZATION immediately to drive ion trapping.',
          '🏥 ACTIVATE EMERGENCY HAEMODIALYSIS: Level exceeds extreme corporate danger caps (>800 mg/L in adults, >700 mg/L in elderly/children).'
        );
      }

      // 🔄 The Core Loop Check: Has the Level Peaked? (tdm5.png bottom instruction)
      if (!peaked) {
        recs.push(
          '🔄 PROTOCOL COMPLIANCE MANDATE: True peak concentration has NOT been demonstrated yet. Repeat salicylate assay level testing every 3 hours until values definitively fall.'
        );
      } else {
        recs.push(
          '📉 PEAK CONFIRMED: Serum concentration has broken its ascending curve and is trending downward. If levels stay safely < 300 mg/L and patient remains completely asymptomatic, discharge tracks can be audited.'
        );
      }
    }

    setTriage({ status, severity, peaked, recommendations: recs, color });

  }, [patientGroup, hoursPostIngestion, hasSevereFeatures, bloodPh, level1, level2, level3]);

  return (
    <div style={{ background: '#111416', minHeight: '100vh', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
              const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
              const trackingLabel = `${actualUserEmail}-printed-Salicylate`;
              logUserActivityQuietly(trackingLabel); 
              window.print();
          }} 
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
      {/* HEADER SECTION */}
      <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '2px solid #e74c3c', paddingBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#e74c3c', fontWeight: '800' }}>SALICYLATE TOXICITY MONITORING SUITE</h2>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', textTransform: 'uppercase' }}>Serial Concentration & Treatment Guidance Worksheet</span>
      </div>
      {/* EXPANDABLE LOCAL HOSPITAL TOXICOLOGY GUIDELINES HANDBOOK PANEL */}
      <details style={{ background: '#1c2226', border: '1px solid #e74c3c', borderRadius: '8px', marginBottom: '25px', padding: '15px', boxSizing: 'border-box' }}>
        <summary style={{ fontWeight: 'bold', color: '#ff7675', cursor: 'pointer', outline: 'none', fontSize: '0.92rem', display: 'flex', alignItems: 'center' }}>
          📋 Check Clinical Sampling Protocols & Toxicity Range Matrices (Click to expand)
        </summary>
        
        <div style={{ marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px', fontSize: '0.85rem', lineHeight: '1.6', color: '#bdc3c7' }}>
          
          {/* CRITICAL PROTOCOL SAMPLING TIMELINE RULES */}
          <div style={{ marginBottom: '15px', background: 'rgba(231, 76, 60, 0.05)', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #e74c3c' }}>
            <strong style={{ color: '#fff', fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>⏱️ Mandatory Sampling Timeline Rules:</strong>
            • <strong>Initial Assessment Base:</strong> Draw blood sample <span style={{ color: '#fff', fontWeight: 'bold' }}>at least 4 hours after ingestion</span>. Levels sampled prior to 4 hours are clinically uninterpretable due to active absorption fluxes.<br />
            • <strong>Serial Monitoring Loop:</strong> Repeat the salicylate concentration assay <span style={{ color: '#fff', fontWeight: 'bold' }}>every 3 hours until the concentration definitively falls</span>. Peak concentrations can be severely delayed (up to 12 hours) with massive or enteric-coated aspirin overdoses.
          </div>

          {/* DYNAMIC HANDBOOK TOXICITY STRATIFICATION MATRIX */}
          <strong style={{ color: '#fff', fontSize: '0.9rem', display: 'block', marginBottom: '6px' }}>📊 Serum Concentration & Common Adverse Effects Matrix:</strong>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            
            {/* MILD SECTOR */}
            <div style={{ background: 'rgba(46, 204, 113, 0.05)', border: '1px solid rgba(46, 204, 113, 0.2)', padding: '10px', borderRadius: '6px' }}>
              <span style={{ color: '#2ecc71', fontWeight: 'bold', display: 'block' }}>🟢 MILD POISONING WINDOW</span>
              <strong>Concentration:</strong> Adults: 300 – 600 mg/L | Children / Elderly: 200 – 450 mg/L<br />
              <span style={{ fontSize: '0.8rem', color: '#a2b9bc' }}><strong>Clinical Features:</strong> Lethargy, nausea, vomiting, tinnitus (ringing in ears), dizziness.</span><br/>
              <span style={{fontSize: '0.8rem', color: '#a2b9bc'}}><strong> Recommended Management:</strong> Rehydrate with oral or intravenous fluids.</span>
            </div>

            {/* MODERATE SECTOR */}
            <div style={{ background: 'rgba(241, 196, 15, 0.05)', border: '1px solid rgba(241, 196, 15, 0.2)', padding: '10px', borderRadius: '6px' }}>
              <span style={{ color: '#f1c40f', fontWeight: 'bold', display: 'block' }}>🟡 MODERATE POISONING WINDOW</span>
              <strong>Concentration:</strong> Adults: 600 – 800 mg/L | Children / Elderly: 450 – 700 mg/L<br />
              <span style={{ fontSize: '0.8rem', color: '#a2b9bc' }}><strong>Clinical Features:</strong> Mild tachypnoea (hyperventilation), hyperpyrexia (fever), sweating, dehydration, loss of coordination, restlessness.</span><br/>
              <span style={{fontSize: '0.8rem', color: '#a2b9bc'}}><strong> Recommended Management:</strong> Urinary ALKALINIZATION.<br/>
              Urinary ALKALINIZATION:<br/>  
              Adults: Give 1L of 1.26% Sodium Bicarbonate with 20 to 40 mmol Potassium as an IV infusion over 3 hours. <br/>
              Children: Dilute 1 ml/kg 8.4% bicarb in 10 ml/kg saline, adding 1 mmol/kg potassium. This should be given at a rate of 2ml/kg/h as an iv infusion.</span>
            </div>

            {/* SEVERE SECTOR */}
            <div style={{ background: 'rgba(231, 76, 60, 0.05)', border: '1px solid rgba(231, 76, 60, 0.2)', padding: '10px', borderRadius: '6px' }}>
              <span style={{ color: '#e74c3c', fontWeight: 'bold', display: 'block' }}>🔴 SEVERE POISONING WINDOW</span>
              <strong>Concentration:</strong> Adults: &gt; 800 mg/L | Children / Elderly: &gt; 700 mg/L<br />
              <span style={{ fontSize: '0.8rem', color: '#a2b9bc' }}><strong>Clinical Features:</strong> Hypotension, significant metabolic acidosis (even after rehydration), acute renal failure (oliguria), and dangerous CNS features (hallucinations, stupor, fits, coma).</span><br/>
              <span style={{fontSize: '0.8rem', color: '#a2b9bc'}}><strong> Recommended Management:</strong> Hemodialysis + Give sodium bicarbonate (cautions with volume if anuric).</span>
            </div>

          </div>

          <div style={{ fontStyle: 'italic', fontSize: '0.72rem', color: '#7f8c8d', marginTop: '10px', textAlign: 'right' }}>
            *Data verified directly from Hospital TDM Reference Guide Sheets.
          </div>
        </div>
      </details>
      {/* STEP 1: PATIENT PROFILE PARAMETERS */}
      <div style={sectionCardStyle}>
        <h3 style={{ color: '#e74c3c', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 1: Patient Profile & Timeline Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={labelStyle}>PATIENT RISK GROUP:</label>
            <select value={patientGroup} onChange={(e) => setPatientGroup(e.target.value)} style={inputStyle}>
              <option value="adult">Standard Adult Patient</option>
              <option value="vulnerable">Vulnerable Patient (Children &lt; 12y or Elderly &gt; 65y)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>HOURS POST-INGESTION:</label>
            <input type="number" placeholder="e.g. 5" value={hoursPostIngestion} onChange={(e) => setHoursPostIngestion(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>ARTERIAL BLOOD GAS pH:</label>
            <input type="number" step="0.01" placeholder="e.g. 7.41" value={bloodPh} onChange={(e) => setBloodPh(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>PRESENCE OF SEVERE FEATURES? (Coma/Convulsions/Pulmonary Edema/Renal Failure):</label>
            <select value={hasSevereFeatures} onChange={(e) => setHasSevereFeatures(e.target.value)} style={{ ...inputStyle, border: hasSevereFeatures === 'yes' ? '1px solid #e74c3c' : '1px solid #34495e' }}>
              <option value="no">No Severe Visualized Features</option>
              <option value="yes">YES - Severe Features Present</option>
            </select>
          </div>
        </div>
      </div>

      {/* STEP 2: SERIAL CONCENTRATION DATA LOOP FIELDS */}
      <div style={sectionCardStyle}>
        <h3 style={{ color: '#3498db', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 2: Serial Salicylate TDM Inputs (Repeated every 3 hours)</h3>
        <p style={{ margin: '-10px 0 15px 0', fontSize: '0.78rem', color: '#bdc3c7' }}>
          *Input your sequential laboratory tracking values. The tracker will evaluate the kinetic curve direction to ensure saturation caps have broken down.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
          <div>
            <label style={labelStyle}>🩸 INITIAL LEVEL 1 (MG/L):</label>
            <input type="number" placeholder="e.g. 350" value={level1} onChange={(e) => setLevel1(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>🔄 LEVEL 2 (+3 HOURS) (MG/L):</label>
            <input type="number" placeholder="Optional" value={level2} onChange={(e) => setLevel2(e.target.value)} style={inputStyle} disabled={!level1} />
          </div>
          <div>
            <label style={labelStyle}>🔄 LEVEL 3 (+6 HOURS) (MG/L):</label>
            <input type="number" placeholder="Optional" value={level3} onChange={(e) => setLevel3(e.target.value)} style={inputStyle} disabled={!level2} />
          </div>
        </div>
      </div>

      {/* STEP 3 & 4: REAL-TIME FLOWCHART TRIAGE HIGHLIGHT PANELS */}
      {level1 && (
        <div style={{ background: '#1c2226', border: `1px solid ${triage.color}`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#bdc3c7', textTransform: 'uppercase' }}>Current Tracker Evaluation Status</span>
              <h2 style={{ margin: '2px 0 0 0', color: triage.color, fontWeight: '900', fontSize: '1.4rem' }}>{triage.status}</h2>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '8px 15px', borderRadius: '6px', textAlign: 'right' }}>
              <span style={{ fontSize: '0.68rem', color: '#95a5a6', display: 'block' }}>GRADED SEVERITY PROFILE:</span>
              <strong style={{ color: triage.color, fontSize: '1.05rem' }}>{triage.severity}</strong>
            </div>
          </div>

          {/* DYNAMIC ACTION DIRECTIVE LISTING */}
          <h4 style={{ margin: '0 0 10px 0', color: '#fff', fontSize: '0.9rem', textTransform: 'uppercase' }}>📋 Target Clinical Directives (Handbook Protocol Actions):</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {triage.recommendations.map((rec, idx) => (
              <div key={idx} style={{ background: '#111416', padding: '12px', borderRadius: '6px', fontSize: '0.82rem', borderLeft: `3px solid ${triage.color}`, lineHeight: '1.5' }}>
                {rec}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
      <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
          *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
      </p>

    </div>
  );
}

// Design Constants
const sectionCardStyle = {
  background: '#1c2226',
  padding: '20px',
  borderRadius: '10px',
  border: '1px solid #2c3539',
  boxSizing: 'border-box',
  marginBottom: '20px'
};

const labelStyle = {
  fontSize: '0.68rem',
  fontWeight: 'bold',
  color: '#95a5a6',
  display: 'block',
  marginBottom: '5px',
  letterSpacing: '0.3px',
  lineHeight: '1.3'
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: '#2c3e50',
  border: '1px solid #34495e',
  color: '#fff',
  borderRadius: '6px',
  fontSize: '0.82rem',
  outline: 'none',
  boxSizing: 'border-box'
};